import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { go } from 'connected-react-router';
import cx from 'classnames';

// Design System
import { Modal } from 'jl-design-system/components/modal/Modal';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import { submitForm } from 'jl-design-system/redux/actions/form/formActions';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ProductProps } from 'types/Product.types';
import { EditBasketModalProps } from './EditBasketModal.types';
import {
  HandleRemoveItemActionTypes,
  UpdateItemQuantityActionTypes,
  ShowItemRemoveOverlayTypes,
} from '@redux/actions/edit-basket/editBasketActions.types';

// Config
import editBasketModalState from './EditBasketModal.state';
import editBasketConstants from '@constants/editBasketConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import {
  closeEditBasketModal,
  handleRemoveItem as handleRemoveItemAction,
  hideItemRemoveOverlay as hideItemRemoveOverlayAction,
  recordFixedBasketFooterImpressionId,
  showItemRemoveOverlay as showItemRemoveOverlayAction,
  updateItemQuantity,
} from '@redux/actions/edit-basket/editBasketActions';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';

// Components
import Container from '@components/container';
import EditBasketItem from '@components/edit-basket-item';
import OrderTotals from '@components/order-totals';
import TradeInDetails from '@components/trade-in-details';
import ThresholdBar from '@components/threshold-bar';

// Styles
import styles from './edit-basket-modal.scss';

const EditBasketModal = ({
  isDeliveryPage = false,
  onLoad = () => {},
  routerActions = { go },
  stepperChangeDebounceMsecs = 500,
}: EditBasketModalProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    editBasketRemoveItemApiCallActiveIds = [],
    editBasketItemOutOfStockOverlayIds = [],
    editBasketItemRemoveOverlayIds = [],
    editBasketItemUpdateActiveIds = [],
    tradeInQuotes = [],
    isProcessing,
    items = [],
    partnerDiscountEnabled,
    thresholdBarEnabled,
    fixedBasketFooterEnabled,
  } = useSelector(editBasketModalState);
  const { isModalSmall } = useWindowSize();

  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [hasQuantityChanged, setHasQuantityChanged] = useState<boolean>(false);

  useEffect(() => {
    onLoad();
    setHasLoaded(true);

    document.addEventListener('keydown', disabledEscKeyHandler);

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler);
    };
  }, []);

  const checkForScrollbar = () => {
    const scrollContainer = document.querySelector(
      fixedBasketFooterEnabled ? '[data-testid="fixed-footer-scroll"]' : '[data-testid="modal-body"]',
    );

    if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
      dispatch(recordFixedBasketFooterImpressionId());
    }
  };

  useEffect(() => {
    if (hasLoaded) {
      checkForScrollbar();
    }
  }, [hasLoaded]);

  const getOverlayType = ({
    itemId,
    editBasketItemRemoveOverlayIds,
    editBasketItemOutOfStockOverlayIds,
    parentHasRemovalOverlay,
  }: any) => {
    if (!parentHasRemovalOverlay) {
      if (editBasketItemRemoveOverlayIds?.includes(itemId)) {
        return editBasketConstants.REMOVE_ITEM;
      }
      if (editBasketItemOutOfStockOverlayIds?.includes(itemId)) {
        return editBasketConstants.OUT_OF_STOCK_ITEM;
      }
    }
    return '';
  };

  const onClose = () => {
    dispatch(closeEditBasketModal());

    if (!isProcessing) {
      dispatch(routerActions.go(-1));
    }
  };

  const getQuantityChangeHandler = (formId: string) => () => {
    dispatch(submitForm(formId));
  };

  const onFormSubmit = ({ itemId, itemQuantity }: UpdateItemQuantityActionTypes) => {
    setHasQuantityChanged(true);
    const { quantity } = items.find((item) => item.id === itemId) ?? {};
    let newQuantity = itemQuantity;
    if (typeof itemQuantity === 'string') {
      newQuantity = parseInt(itemQuantity, 10);
    }
    if (newQuantity === quantity) return null;

    return dispatch(updateItemQuantity({ itemId, quantity: newQuantity }));
  };

  const handleRemoveItem = ({
    forceRemove,
    isAgeVerification,
    isChildItem,
    isTradeIn,
    itemId,
    lastItemInBasket,
    isExpressCheckout,
  }: HandleRemoveItemActionTypes) => {
    dispatch(
      handleRemoveItemAction({
        forceRemove,
        itemId,
        lastItemInBasket,
        isChildItem,
        isTradeIn,
        isAgeVerification,
        isExpressCheckout,
      }),
    );
  };

  const hideItemRemoveOverlay = (itemId: string) => {
    dispatch(hideItemRemoveOverlayAction(itemId));
  };

  const showItemRemoveOverlay = ({ itemId, isRemove = false }: ShowItemRemoveOverlayTypes) => {
    dispatch(showItemRemoveOverlayAction({ itemId, isRemove }));
  };

  const renderTradeInContent = () => (
    <TradeInDetails
      handleRemoveItem={handleRemoveItem}
      hideItemRemoveOverlay={hideItemRemoveOverlay}
      isModalView
      isProcessing={isProcessing}
      showItemRemoveOverlay={showItemRemoveOverlay}
      tradeInItems={tradeInQuotes}
    />
  );

  const renderChildItem = ({
    isFirstChild,
    isProcessing,
    item,
    parentQuantity,
    parentHasRemovalOverlay,
    isOutOfStockOverlayActive,
  }: any) => {
    const overlayType = getOverlayType({
      itemId: item?.id,
      editBasketItemRemoveOverlayIds,
      editBasketItemOutOfStockOverlayIds,
      parentHasRemovalOverlay,
    });
    const isItemUpdating = item.id !== undefined && editBasketItemUpdateActiveIds.includes(item.id);
    const removeItemApiCallActive = item.id !== undefined && editBasketRemoveItemApiCallActiveIds.includes(item.id);

    return (
      <EditBasketItem
        key={item.id}
        disabled={isProcessing || isOutOfStockOverlayActive}
        handleQuantityChange={getQuantityChangeHandler}
        handleRemoveItem={handleRemoveItem}
        hideItemRemoveOverlay={hideItemRemoveOverlay}
        isChildItem
        isFirstChild={isFirstChild}
        isItemUpdating={isItemUpdating}
        isProcessing={isProcessing}
        item={item}
        maxValue={parentQuantity}
        onFormSubmit={onFormSubmit}
        overlayType={overlayType}
        parentQuantity={parentQuantity}
        partnerDiscountEnabled={partnerDiscountEnabled}
        removeItemApiCallActive={removeItemApiCallActive}
        showItemRemoveOverlay={showItemRemoveOverlay}
        stepperChangeDebounceMsecs={stepperChangeDebounceMsecs}
      />
    );
  };

  if (!items.length) {
    return null;
  }

  const renderItem = (item: ProductProps, isOutOfStockOverlayActive: boolean, isLast: boolean = false) => {
    const lastItemInBasket = items.length === 1;
    const overlayType = getOverlayType({
      itemId: item.id,
      editBasketItemRemoveOverlayIds,
      editBasketItemOutOfStockOverlayIds,
    });
    const isItemUpdating = item.id !== undefined && editBasketItemUpdateActiveIds.includes(item.id);
    const removeItemApiCallActive = item.id !== undefined && editBasketRemoveItemApiCallActiveIds.includes(item.id);

    return (
      <EditBasketItem
        key={item.id}
        disabled={isProcessing || isOutOfStockOverlayActive}
        handleQuantityChange={getQuantityChangeHandler}
        handleRemoveItem={handleRemoveItem}
        hideItemRemoveOverlay={hideItemRemoveOverlay}
        isItemUpdating={isItemUpdating}
        isProcessing={isProcessing}
        item={item}
        lastItemInBasket={lastItemInBasket}
        lastItemInList={isLast}
        onFormSubmit={onFormSubmit}
        overlayType={overlayType}
        partnerDiscountEnabled={partnerDiscountEnabled}
        removeItemApiCallActive={removeItemApiCallActive}
        showItemRemoveOverlay={showItemRemoveOverlay}
        stepperChangeDebounceMsecs={stepperChangeDebounceMsecs}
      >
        {(item.childItems || []).map((childItem, index) =>
          renderChildItem({
            isFirstChild: index === 0,
            isProcessing,
            item: childItem,
            parentQuantity: item.quantity,
            parentHasRemovalOverlay: !!overlayType,
            isOutOfStockOverlayActive,
          }),
        )}
      </EditBasketItem>
    );
  };

  const renderModalContent = () => {
    const isOutOfStockOverlayActive = !!editBasketItemOutOfStockOverlayIds.length;

    const containerProps = fixedBasketFooterEnabled ? ({ flex: { flexDirection: 'col' }, width: 'full' } as const) : {};
    const scrollContainerProps = fixedBasketFooterEnabled
      ? ({ className: styles.scrollbar, padding: isModalSmall ? '2' : '3' } as const)
      : {};
    const innerContainerProps = fixedBasketFooterEnabled
      ? ({ paddingBottom: '3', paddingX: isModalSmall ? '2' : '3', testId: 'fixed-footer-container' } as const)
      : {};

    return (
      <Container relative testId="edit-basket-modal" {...containerProps}>
        <Container testId="fixed-footer-scroll" {...scrollContainerProps}>
          {renderTradeInContent()}
          {items.map((item, index) => {
            const isLast = index === (items?.length ?? 0) - 1;
            return renderItem(item, isOutOfStockOverlayActive, isLast);
          })}
        </Container>
        <Container background="white" borderTop="grey-15" paddingTop="3" relative {...innerContainerProps}>
          {fixedBasketFooterEnabled && <div className={styles.fade} />}
          <Container marginBottom="2">
            <OrderTotals editBasketModal isDeliveryPage={isDeliveryPage} />
          </Container>
          {thresholdBarEnabled && <ThresholdBar hasQuantityChanged={hasQuantityChanged} placementLocation="basket" />}
          <PrimaryButton
            className="mb-0"
            data-testid="edit-basket-modal-done-button"
            disabled={isProcessing || isOutOfStockOverlayActive}
            onClick={onClose}
          >
            Done
          </PrimaryButton>
        </Container>
      </Container>
    );
  };

  return (
    hasLoaded && (
      <Modal
        className={cx(styles.modal, {
          [styles.isProcessing]: isProcessing,
          [styles.hasFixedBasketFooter]: fixedBasketFooterEnabled,
        })}
        footer={false}
        isOpen
        onClose={onClose}
        title="Basket"
      >
        {renderModalContent()}
      </Modal>
    )
  );
};

export default EditBasketModal;
